"use client";

import { useEffect, useState } from "react";
import { createLogger } from "@/lib/logger";
import HorizontalScroll from "@/app/components/HorizontalScroll";
import { useAuth } from "@/hooks/useAuth";
import { useUserMe } from "@/contexts/UserMeContext";
import { usePredictEnabled } from "@/hooks/usePredictEnabled";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useHyperliquidBalance } from "@/hooks/useHyperliquidBalance";
import {
  FiCopy,
  FiCheck,
  FiZap,
  FiAlertTriangle,
  FiShare2,
  FiRefreshCw,
  FiTrendingUp,
  FiTrendingDown,
  FiAward,
  FiActivity,
  FiDollarSign,
  FiGift,
} from "react-icons/fi";
import { cdn } from "@/lib/cdn";
import { authFetch } from "@/lib/authFetch";
import { useRedeemableQuery } from "@/hooks/useRedeemableQuery";
import Link from "next/link";
import ShareStatsModal from "@/app/components/ShareStatsModal";

import AnimatedNumber from "@/app/components/AnimatedNumber";
import { formatCurrency, formatCompactCurrency } from "@/lib/format";
import { useT } from "@/hooks/useT";

const logger = createLogger("PortfolioProfileBar");

interface PortfolioStats {
  totalPnL: number;
  realizedPnl: number;
  unrealizedPnl: number;
  openPositions: number;
  closedPositions: number;
  winCount: number;
  lossCount: number;
  winRate: number;
  bestTrade: number;
  roi: number;
  totalInvested: number;
}

interface VolumeStats {
  volume: number;
  tradeCount: number;
}

interface UserProfile {
  username: string | null;
  name: string;
  image: string;
}

interface PortfolioProfileBarProps {
  onRefresh?: () => void;
  /** Valor REAL de las posiciones PM, de useUnifiedPositions (misma fuente que
   *  la pestaña Positions + las wallet cards). Cuando se provee, el hero lo usa
   *  en vez del positions.totalCurrentValue de /api/portfolio/all — que viene de
   *  otra fuente (DB) y queda inflado tras vender/redimir (cuenta posiciones que
   *  ya no tienes), haciendo que el total grande doble-contara con el cash.
   *  Single source of truth (CLAUDE.md consistencia de datos). */
  pmPositionsValue?: number;
  kalshiPositionsValue?: number;
  solanaBalance?: number | null;
  /** Predict.fun positions value. Currently NOT rendered in the balance
   *  bar/breakdown (HL replaced Predict) but the prop stays wired because
   *  we plan to re-enable Predict soon — keeping the plumbing avoids
   *  re-threading it through the page when that happens. */
  predictPositionsValue?: number;
  /** Predict.fun available balance (BNB-side USDT). Same rationale as
   *  predictPositionsValue — kept wired for the imminent re-enable. */
  bnbUsdtBalance?: number | null;
  /** Open positions count derived from the unified positions hook (same
   *  source the per-wallet cards use). Overrides the backend stat when
   *  provided so the "X abiertas" line agrees with the per-wallet
   *  counts below — backend `stats.openPositions` includes Predict
   *  positions that have no wallet card, producing ghost numbers. */
  unifiedOpenCount?: number;
  /** Closed positions count from the unified positions list. Paired
   *  with unifiedOpenCount so the open/closed pill tells one story. */
  unifiedClosedCount?: number;
}

export default function PortfolioProfileBar({ onRefresh, pmPositionsValue, kalshiPositionsValue = 0, solanaBalance, predictPositionsValue, bnbUsdtBalance, unifiedOpenCount, unifiedClosedCount }: PortfolioProfileBarProps) {
  const t = useT();
  const { isLoggedIn, isReady, currentWalletAddress, user: privyUser } = useAuth();
  const { user: userMe } = useUserMe();
  const predictEnabled = usePredictEnabled();
  // The hero is Polymarket-centric (balance, PnL, "Trading On"). Show the
  // Polymarket DEPOSIT address (where funds land) — the migrated deposit
  // wallet if present, else the Safe — not the Privy login EOA. Falls back to
  // the login wallet only when no Polymarket trading wallet exists.
  const depositAddress: string | null =
    ((userMe as any)?.wallet_type === "deposit" && (userMe as any)?.deposit_wallet_address) ||
    (userMe as any)?.safe_address ||
    currentWalletAddress ||
    null;
  const { balance: usdcBalance, tradingEnabled } = usePolymarketBalanceContext();
  // HL balance + Arbitrum-side USDC + open-position notional. The hook
  // reads /api/trading/hyperliquid/balance + /wallet-balance + /positions
  // so the header picks up the full HL footprint once the user has
  // onboarded. We sum HL margin + Arbitrum so USDC sitting on the EOA
  // before the bridge-to-HL step still counts toward the total balance —
  // matches the way WalletCardsSection.tsx renders the wallet card
  // ("USDC (HL + Arbitrum)") and avoids the bug where the breakdown
  // showed only the HL slice while the card showed the full sum.
  const { hlBalance, arbitrumBalance, portfolioValue: hlPortfolioValue } = useHyperliquidBalance();

  const [stats, setStats] = useState<PortfolioStats | null>(null);
  const [volumeStats, setVolumeStats] = useState<VolumeStats | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const { data: redeemableData } = useRedeemableQuery();
  const [positionsValue, setPositionsValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Tracks whether the most recent /api/portfolio/all attempt failed.
  // Surfaced as a soft banner so users know the numbers they see may be
  // stale instead of silently rendering $0 across the bar (which we
  // would otherwise do when stats remain null after a failed fetch).
  const [fetchError, setFetchError] = useState(false);

  const getPrivyProfileImage = () => {
    if (!privyUser) return null;
    return (privyUser.google as { profilePictureUrl?: string } | undefined)?.profilePictureUrl
      || (privyUser.twitter as { profilePictureUrl?: string } | undefined)?.profilePictureUrl
      || (privyUser.discord as { avatarUrl?: string } | undefined)?.avatarUrl
      || privyUser.farcaster?.pfp
      || (privyUser.telegram as { photo_url?: string } | undefined)?.photo_url
      || null;
  };

  useEffect(() => {
    if (!isReady || !isLoggedIn) {
      setLoading(false);
      return;
    }

    // `silent` skips the skeleton: trade-triggered refreshes swap the
    // numbers in place so the bar never flashes a loading state mid-session.
    const fetchData = async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setFetchError(false);
        const batchRes = await authFetch("/api/portfolio/all");

        if (batchRes.ok) {
          const data = await batchRes.json();

          setStats({
            totalPnL: data.stats?.totalPnL || 0,
            realizedPnl: data.stats?.realizedPnl || 0,
            unrealizedPnl: data.stats?.unrealizedPnl || 0,
            openPositions: data.stats?.openPositions || 0,
            closedPositions: data.stats?.closedPositions || 0,
            winCount: data.stats?.winCount || 0,
            lossCount: data.stats?.lossCount || 0,
            winRate: data.stats?.winRate || 0,
            bestTrade: data.stats?.bestTrade || 0,
            roi: data.stats?.roi || 0,
            totalInvested: data.stats?.totalInvested || 0,
          });

          if (data.profile) {
            setUserProfile({
              username: data.profile.username,
              name: data.profile.username || "",
              image: data.profile.profileImage || "",
            });
          }

          if (data.volume) {
            setVolumeStats({
              volume: data.volume.volume || 0,
              tradeCount: data.volume.tradeCount || 0,
            });
          }

          if (data.positions) {
            setPositionsValue(data.positions.totalCurrentValue || 0);
          }
        } else {
          // Non-2xx — keep the previous stats (don't zero-out the bar)
          // but flip the error flag so the soft banner appears.
          setFetchError(true);
        }

      } catch (err) {
        logger.error("Error fetching profile data", { error: err instanceof Error ? err.message : String(err) });
        setFetchError(true);
      } finally {
        if (!silent) setLoading(false);
      }
    };

    fetchData();

    // Re-pull stats + positionsValue on every trade so the hero balance stays
    // consistent with the cash balance (PolymarketBalanceContext), which
    // already refreshes on trades. Without this a SELL bumps the cash
    // immediately while positionsValue still counts the position the user just
    // closed — the total double-counts ("cuenta más de lo que tengo") until a
    // reload. Retry on a short ladder because trade indexing isn't atomic with
    // execution (same pattern as MyPositions).
    const refreshTimeouts = new Set<ReturnType<typeof setTimeout>>();
    const handleTradeEvent = () => {
      fetchData(true);
      [1500, 4000].forEach((delay) => {
        const id = setTimeout(() => { refreshTimeouts.delete(id); fetchData(true); }, delay);
        refreshTimeouts.add(id);
      });
    };
    window.addEventListener("portfolio:refresh", handleTradeEvent);
    window.addEventListener("positions:refresh", handleTradeEvent);

    return () => {
      refreshTimeouts.forEach((id) => clearTimeout(id));
      window.removeEventListener("portfolio:refresh", handleTradeEvent);
      window.removeEventListener("positions:refresh", handleTradeEvent);
    };
  }, [isReady, isLoggedIn, currentWalletAddress]);

  const truncateAddress = (address: string) => `${address.slice(0, 6)}...${address.slice(-4)}`;

  const copyAddress = async () => {
    if (depositAddress) {
      await navigator.clipboard.writeText(depositAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading || !isReady) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-5 border border-gray-200 dark:border-[#1A1A1A] animate-pulse">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-[5px] bg-gray-200 dark:bg-[#1A1A1A]" />
          <div className="flex-1">
            <div className="h-4 w-28 bg-gray-200 dark:bg-[#1A1A1A] rounded mb-2" />
            <div className="h-3 w-20 bg-gray-200 dark:bg-[#1A1A1A] rounded" />
          </div>
        </div>
        <div className="h-3 rounded-[5px] bg-gray-200 dark:bg-[#1A1A1A] mb-5" />
        <HorizontalScroll className="flex sm:grid sm:overflow-visible grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 shrink-0 w-[150px] sm:w-auto bg-gray-100 dark:bg-[#0f0f12] rounded-[5px]" />
          ))}
        </HorizontalScroll>
      </div>
    );
  }

  const pmAvailable = usdcBalance || 0;
  // Valor de posiciones PM: prioriza el prop de useUnifiedPositions (mismo
  // origen que la pestaña Positions + wallet cards) sobre el local de
  // /api/portfolio/all, que queda inflado tras vender/redimir → doble conteo.
  const heroPositionsValue = pmPositionsValue ?? positionsValue;
  const solAvailable = solanaBalance || 0;
  // HL "available" = margin already on HL + USDC on Arbitrum ready to
  // bridge. The wallet card on this same page uses the same sum.
  const hlAvailable = (hlBalance || 0) + (arbitrumBalance || 0);
  const hlPositionsValueLocal = hlPortfolioValue || 0;
  const toClaim = redeemableData?.totalRedeemable || 0;
  // HL replaces Predict in the visible breakdown. Predict balances are
  // excluded from totalBalance too — if the source isn't rendered in the
  // bar + breakdown row, the hero number must not silently include it
  // (otherwise users see a total that doesn't match the sum of the
  // labelled chips below).
  const totalBalance =
    pmAvailable + solAvailable + hlAvailable
    + heroPositionsValue + kalshiPositionsValue + hlPositionsValueLocal
    // Predict re-included in the hero total gated by ENABLE_PREDICT (matches the
    // bar/legend below, which also render Predict only when the flag is on).
    + (predictEnabled ? ((bnbUsdtBalance ?? 0) + (predictPositionsValue ?? 0)) : 0)
    + toClaim;
  const totalPnL = stats?.totalPnL || 0;
  const totalPnLPercent = stats?.totalInvested && stats.totalInvested > 0
    ? (totalPnL / stats.totalInvested) * 100
    : 0;
  const isPositive = totalPnL >= 0;
  const bestTrade = stats?.bestTrade || 0;
  // For a user with no resolved trades yet, `bestTrade` is 0 — show
  // it as a neutral "$0.00" without a misleading "+" prefix or green
  // colour. `isBestPositive` only flips to true on a real positive PnL.
  const hasBestTrade = bestTrade !== 0;
  const isBestPositive = bestTrade > 0;
  // Win rate: integer when whole (31%), one decimal when fractional
  // (30.5%) — avoids rounding 30.5% to 31% on the hero card while
  // keeping the common case clean.
  const winRateValue = stats?.winRate || 0;
  const winRateDecimals = Number.isInteger(winRateValue) ? 0 : 1;
  const avatarSrc = userProfile?.image || getPrivyProfileImage() || cdn("/frenpfp.png");
  const displayName = userProfile?.username || userProfile?.name || t("portfolio.trader");
  // Avatar + name link to the user's own FrenFlow social profile. Username is
  // the canonical handle (displayName can fall back to a generic label, so
  // gate on the real username — no username, no link).
  const profileHref = userProfile?.username ? `/profile/${userProfile.username}` : null;

  // Balance bar percentages (PM + Kalshi + Hyperliquid + ToClaim).
  // Predict is hidden per product call — the segment, the legend chip,
  // and the percent calc all skip it so the bar visually reflects what's
  // actually labelled below. Percentages are computed against the
  // *visible* slices only (not totalBalance) — when the user holds
  // residual Predict balance, totalBalance still includes it so the
  // hero number is correct, but using it as the denominator here would
  // leave a visible gap in the bar (segments summing to <100%). The
  // legend chips and the bar segments now always tell the same story.
  const pmTotal = pmAvailable + heroPositionsValue;
  const kalshiTotal = solAvailable + kalshiPositionsValue;
  const hlTotal = hlAvailable + hlPositionsValueLocal;
  // Predict re-enabled gated by ENABLE_PREDICT (segment + legend + percent).
  const predictAvailable = predictEnabled ? (bnbUsdtBalance ?? 0) : 0;
  const predictPosVal = predictEnabled ? (predictPositionsValue ?? 0) : 0;
  const predictTotal = predictAvailable + predictPosVal;
  const visibleTotal = pmTotal + kalshiTotal + hlTotal + predictTotal + toClaim;
  const pmPercent = visibleTotal > 0 ? (pmTotal / visibleTotal) * 100 : 50;
  const kalshiPercent = visibleTotal > 0 ? (kalshiTotal / visibleTotal) * 100 : 0;
  const hlPercent = visibleTotal > 0 ? (hlTotal / visibleTotal) * 100 : 0;
  const predictPercent = visibleTotal > 0 ? (predictTotal / visibleTotal) * 100 : 0;
  const toClaimPercent = visibleTotal > 0 ? (toClaim / visibleTotal) * 100 : 0;

  return (
    <>
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors overflow-hidden">
        {/* Profile row */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {profileHref ? (
              <Link href={profileHref} aria-label={`${displayName} profile`}>
                <img src={avatarSrc} alt="" className="w-12 h-12 rounded-[5px] object-cover hover:opacity-90 transition-opacity" />
              </Link>
            ) : (
              <img src={avatarSrc} alt="" className="w-12 h-12 rounded-[5px] object-cover" />
            )}
            <div className={`absolute -bottom-0.5 -right-0.5 w-4.5 h-4.5 rounded-[5px] border-2 border-white dark:border-[#18181D] flex items-center justify-center pointer-events-none ${
              tradingEnabled ? "bg-[#00FF9D]" : "bg-[#FF1744]"
            }`}>
              {tradingEnabled && <FiCheck className="w-2.5 h-2.5 text-black" />}
            </div>
          </div>

          {/* Name + wallet */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {profileHref ? (
                <Link href={profileHref} className="font-monument text-base text-gray-900 dark:text-white truncate hover:underline">{displayName}</Link>
              ) : (
                <span className="font-monument text-base text-gray-900 dark:text-white truncate">{displayName}</span>
              )}
              <Link
                href="/settings?tab=trading"
                className={`hidden sm:flex items-center gap-1 px-2 py-0.5 rounded-[5px] text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                  tradingEnabled
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                    : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                }`}
              >
                {tradingEnabled ? <><FiZap className="w-2.5 h-2.5" /> {t("portfolio.profile.tradingOn")}</> : <><FiAlertTriangle className="w-2.5 h-2.5" /> {t("portfolio.profile.enableTrading")}</>}
              </Link>
            </div>
            {depositAddress && (
              <button onClick={copyAddress} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mt-0.5">
                <span className="font-mono">{truncateAddress(depositAddress)}</span>
                {copied ? <FiCheck className="w-3 h-3 text-[#00FF9D]" /> : <FiCopy className="w-3 h-3" />}
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            {onRefresh && (
              <button onClick={onRefresh} className="p-2 rounded-[5px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#0f0f12] transition-colors" title={t("portfolio.profile.refresh")}>
                <FiRefreshCw className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setShareModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#0f0f12] hover:bg-gray-200 dark:hover:bg-[#1A1A1A] transition-colors"
            >
              <FiShare2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t("portfolio.profile.share")}</span>
            </button>
          </div>
        </div>

        {/* Soft banner — only when the last /api/portfolio/all fetch
            failed AND we have no cached stats to fall back to. Lets the
            user know the bar might be stale instead of silently showing
            zeros for everything (which is what they'd see otherwise,
            since stats stays at null). Auto-dismisses on the next
            successful refetch (refresh button or websocket-triggered). */}
        {fetchError && !stats && (
          <div className="mx-5 mb-3 flex items-start gap-2 rounded-[5px] border border-amber-300/60 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-700 dark:border-amber-300/20 dark:bg-amber-400/10 dark:text-amber-300">
            <FiAlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{t("portfolio.profile.fetchError")}</span>
          </div>
        )}

        {/* Balance section */}
        <div className="px-5 pb-4">
          {/* Hero balance */}
          <div className="flex items-baseline gap-3 mb-3">
            <span className="font-monument text-3xl text-gray-900 dark:text-white tabular-nums">$<AnimatedNumber value={totalBalance} decimals={2} /></span>
            <span className={`text-sm font-medium flex items-center gap-1 tabular-nums ${isPositive ? "text-emerald-600 dark:text-[#00FF9D]" : "text-red-600 dark:text-[#FF1744]"}`}>
              {isPositive ? <FiTrendingUp className="w-3.5 h-3.5" /> : <FiTrendingDown className="w-3.5 h-3.5" />}
              {isPositive ? "+" : "-"}$<AnimatedNumber value={Math.abs(totalPnL)} decimals={2} />
              <span className="opacity-60">({totalPnLPercent >= 0 ? "+" : "-"}<AnimatedNumber value={Math.abs(totalPnLPercent)} decimals={1} />%)</span>
            </span>
          </div>

          {/* Balance bar */}
          <div className="h-2.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] overflow-hidden flex mb-3">
            {pmPercent > 0 && (
              <div className="h-full bg-[#2E5CFF]" style={{ width: `${pmPercent}%` }} />
            )}
            {kalshiPercent > 0 && (
              <div className="h-full bg-[#09C285]" style={{ width: `${kalshiPercent}%` }} />
            )}
            {hlPercent > 0 && (
              <div className="h-full bg-[#97FCE4]" style={{ width: `${hlPercent}%` }} />
            )}
            {predictPercent > 0 && (
              <div className="h-full bg-[#8B5CF6]" style={{ width: `${predictPercent}%` }} />
            )}
            {toClaimPercent > 0 && (
              <div className="h-full bg-amber-400 dark:bg-amber-300" style={{ width: `${toClaimPercent}%` }} />
            )}
          </div>

          {/* Balance breakdown by source */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-[5px] bg-[#2E5CFF]" />
              <span className="font-semibold text-gray-900 dark:text-white"><span className="sm:hidden">PM</span><span className="hidden sm:inline">Polymarket</span></span>
              <span className="text-gray-600 dark:text-gray-300">{formatCurrency(pmAvailable)}</span>
              {heroPositionsValue > 0 && <span className="text-gray-400">({t("portfolio.profile.percentPos", { p: Math.round(heroPositionsValue / (pmTotal || 1) * 100) })})</span>}
            </div>
            {(solAvailable > 0 || kalshiPositionsValue > 0) && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-[5px] bg-[#09C285]" />
                  <span className="font-semibold text-gray-900 dark:text-white">Kalshi</span>
                  <span className="text-gray-600 dark:text-gray-300">{formatCurrency(solAvailable)}</span>
                  {kalshiPositionsValue > 0 && <span className="text-gray-400">({t("portfolio.profile.percentPos", { p: Math.round(kalshiPositionsValue / (kalshiTotal || 1) * 100) })})</span>}
                </div>
              </>
            )}
            {(hlAvailable > 0 || hlPositionsValueLocal > 0) && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-[5px] bg-[#97FCE4]" />
                  <span className="font-semibold text-gray-900 dark:text-white"><span className="sm:hidden">HL</span><span className="hidden sm:inline">Hyperliquid</span></span>
                  <span className="text-gray-600 dark:text-gray-300">{formatCurrency(hlAvailable)}</span>
                  {hlPositionsValueLocal > 0 && <span className="text-gray-400">({t("portfolio.profile.percentPos", { p: Math.round(hlPositionsValueLocal / (hlTotal || 1) * 100) })})</span>}
                </div>
              </>
            )}
            {(predictAvailable > 0 || predictPosVal > 0) && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-[5px] bg-[#8B5CF6]" />
                  <span className="font-semibold text-gray-900 dark:text-white">Predict</span>
                  <span className="text-gray-600 dark:text-gray-300">{formatCurrency(predictAvailable)}</span>
                  {predictPosVal > 0 && <span className="text-gray-400">({t("portfolio.profile.percentPos", { p: Math.round(predictPosVal / (predictTotal || 1) * 100) })})</span>}
                </div>
              </>
            )}
            {toClaim > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <div className="inline-flex items-center gap-1.5 rounded-[5px] border border-amber-300/60 bg-amber-50 px-2.5 py-1 text-amber-700 dark:border-amber-300/20 dark:bg-amber-400/10 dark:text-amber-300">
                  <FiGift className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="font-semibold tabular-nums">{formatCurrency(toClaim)}</span>
                  <span className="font-semibold text-amber-700/80 dark:text-amber-200/80">{t("portfolio.profile.toClaim")}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <HorizontalScroll fadeFrom="bg-white dark:bg-[#18181D]" className="flex sm:grid sm:overflow-visible grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100 dark:bg-[#0f0f12] border-t border-gray-100 dark:border-[#0f0f12]">
          {/* Win Rate */}
          <div className="bg-white dark:bg-[#18181D] p-2.5 sm:p-4 shrink-0 w-[150px] sm:w-auto">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-1.5">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center">
                <FiAward className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500 dark:text-gray-400" />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t("portfolio.profile.winRate")}</span>
            </div>
            <div className="font-monument text-base sm:text-xl text-gray-900 dark:text-white tabular-nums"><AnimatedNumber value={winRateValue} decimals={winRateDecimals} />%</div>
            <div className="text-[10px] text-gray-400 mt-0.5 tabular-nums"><AnimatedNumber value={stats?.winCount || 0} />W / <AnimatedNumber value={stats?.lossCount || 0} />L</div>
          </div>

          {/* Best Trade — neutral grey when user hasn't resolved a trade
              yet (was rendering as a red "-$0.00" which read like a
              loss). Coloured + signed only when there's a real PnL. */}
          <div className="bg-white dark:bg-[#18181D] p-2.5 sm:p-4 shrink-0 w-[150px] sm:w-auto">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-1.5">
              <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-[5px] flex items-center justify-center ${
                !hasBestTrade ? "bg-gray-100 dark:bg-[#0f0f12]"
                  : isBestPositive ? "bg-emerald-50 dark:bg-[#00FF9D]/10"
                    : "bg-red-50 dark:bg-[#FF1744]/10"
              }`}>
                <FiTrendingUp className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${
                  !hasBestTrade ? "text-gray-400 dark:text-gray-500"
                    : isBestPositive ? "text-emerald-600 dark:text-[#00FF9D]"
                      : "text-red-600 dark:text-[#FF1744]"
                }`} />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t("portfolio.profile.bestTrade")}</span>
            </div>
            <div className={`font-monument text-base sm:text-xl tabular-nums ${
              !hasBestTrade ? "text-gray-500 dark:text-gray-400"
                : isBestPositive ? "text-emerald-600 dark:text-[#00FF9D]"
                  : "text-red-600 dark:text-[#FF1744]"
            }`}>
              {hasBestTrade ? (isBestPositive ? "+" : "-") : ""}$<AnimatedNumber value={Math.abs(bestTrade)} decimals={2} />
            </div>
          </div>

          {/* Trades */}
          <div className="bg-white dark:bg-[#18181D] p-2.5 sm:p-4 shrink-0 w-[150px] sm:w-auto">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-1.5">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center">
                <FiActivity className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500 dark:text-gray-400" />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t("portfolio.profile.trades")}</span>
            </div>
            <div className="font-monument text-base sm:text-xl text-gray-900 dark:text-white tabular-nums"><AnimatedNumber value={volumeStats?.tradeCount || 0} /></div>
            <div className="text-[10px] text-gray-400 mt-0.5 tabular-nums">{t("portfolio.profile.openClosed", { open: unifiedOpenCount ?? stats?.openPositions ?? 0, closed: unifiedClosedCount ?? stats?.closedPositions ?? 0 })}</div>
          </div>

          {/* Volume */}
          <div className="bg-white dark:bg-[#18181D] p-2.5 sm:p-4 shrink-0 w-[150px] sm:w-auto">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-1.5">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center">
                <FiDollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500 dark:text-gray-400" />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t("portfolio.profile.volume")}</span>
            </div>
            <div className="font-monument text-base sm:text-xl text-gray-900 dark:text-white tabular-nums">$<AnimatedNumber value={volumeStats?.volume || 0} decimals={2} /></div>
          </div>
        </HorizontalScroll>
      </div>

      {/* Share Modal */}
      <ShareStatsModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        stats={{
          username: displayName.startsWith("@") ? displayName : `@${displayName}`,
          userAvatar: avatarSrc,
          totalVolume: volumeStats?.volume || 0,
          totalTrades: volumeStats?.tradeCount || 0,
          totalPnL,
          unrealizedPnL: stats?.unrealizedPnl || 0,
          realizedPnL: stats?.realizedPnl || 0,
          roi: totalPnLPercent,
          openPositions: unifiedOpenCount ?? stats?.openPositions ?? 0,
          portfolioValue: totalBalance,
          winCount: stats?.winCount || 0,
          lossCount: stats?.lossCount || 0,
          winRate: stats?.winRate || 0,
          bestTrade: stats?.bestTrade || 0,
        }}
      />
    </>
  );
}
